/**
 * Defers router rendering until global loading completes
 * Handles auth redirects for protected routes
 */
import { type ReactNode, useEffect, useMemo } from 'react'

import { useAuth } from '@/hooks/useAuth'
import { useLoading } from '@/hooks/useLoading'

interface DeferredRouterProps {
  /** The Router component to render after loading completes */
  router: ReactNode
  /** Function to determine path information */
  getPathInfo: () => {
    isProtectedRoute: boolean
    isAuthCallback: boolean
    isUnauthenticatedRoute: boolean
  }
}

/**
 * Wrapper component that defers Router rendering until the global loader completes.
 * This prevents pages from mounting and making HTTP requests during the logo animation,
 * reducing CPU/GPU competition and improving animation frame rates and page painting.
 */
export function DeferredRouter({ router, getPathInfo }: DeferredRouterProps): ReactNode {
  const { isAuthenticated } = useAuth()
  const { isGlobalLoaderActive } = useLoading()

  // Compute path info once
  const pathInfo = useMemo(() => getPathInfo(), [getPathInfo])

  // Redirect immediately when auth is resolved as false on protected routes
  // This prevents Router from ever rendering, eliminating dashboard flash
  useEffect(() => {
    if (!pathInfo.isProtectedRoute) {
      return
    }

    if (isAuthenticated === false) {
      window.location.replace('/sign-in')
    }
  }, [isAuthenticated, pathInfo.isProtectedRoute])

  // Block Router on /auth/callback, when loader is active, or when auth is unresolved on protected routes
  // Blocking when isAuthenticated === false prevents Router from rendering before redirect completes
  const isAuthUnresolved = pathInfo.isProtectedRoute && (isAuthenticated === undefined || isAuthenticated === false)
  const shouldBlockRouter = pathInfo.isAuthCallback || isGlobalLoaderActive || isAuthUnresolved

  // Don't render Router until global loader completes
  if (shouldBlockRouter) {
    return null
  }

  return <>{router}</>
}
